import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet.tsx';
import { Button } from '../ui/button.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import type { RefreshableMetric } from './registry.ts';

interface Props {
  metric: RefreshableMetric;
  open: boolean;
  onClose: () => void;
  onSubmit: (params: Record<string, string>) => void;
}

export function RefreshDrawer({ metric, open, onClose, onSubmit }: Props) {
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{metric.displayName}</SheetTitle>
          <SheetDescription>{metric.description}</SheetDescription>
        </SheetHeader>
        <ParamForm key={metric.id} metric={metric} onSubmit={onSubmit} />
      </SheetContent>
    </Sheet>
  );
}

function ParamForm({
  metric,
  onSubmit,
}: {
  metric: RefreshableMetric;
  onSubmit: (params: Record<string, string>) => void;
}) {
  if (metric.params.kind === 'force') return <ForceForm onSubmit={onSubmit} />;
  if (metric.params.kind === 'backfill') {
    return (
      <BackfillForm
        defaultDays={metric.params.defaultDays}
        maxDays={metric.params.maxDays}
        onSubmit={onSubmit}
      />
    );
  }
  if (metric.params.kind === 'month') return <MonthForm onSubmit={onSubmit} />;
  return (
    <div className='pt-4'>
      <Button onClick={() => onSubmit({})}>Confirm refresh</Button>
    </div>
  );
}

function ForceForm({ onSubmit }: { onSubmit: (p: Record<string, string>) => void }) {
  const [force, setForce] = useState(false);
  return (
    <div className='pt-4 flex flex-col gap-3'>
      <div className='flex items-center gap-2'>
        <Checkbox
          id='force'
          checked={force}
          onCheckedChange={(v) => setForce(v === true)}
        />
        <Label htmlFor='force'>Force (bypass existence check, overwrite stored matrix)</Label>
      </div>
      <Button onClick={() => onSubmit({ force: force ? 'true' : 'false' })}>
        Confirm refresh
      </Button>
    </div>
  );
}

function BackfillForm({
  defaultDays,
  maxDays,
  onSubmit,
}: {
  defaultDays: number;
  maxDays: number;
  onSubmit: (p: Record<string, string>) => void;
}) {
  const [days, setDays] = useState(String(defaultDays));
  const [error, setError] = useState<string | null>(null);

  function submit() {
    const n = Number(days);
    if (!Number.isFinite(n) || n < 1 || n > maxDays) {
      setError(`Days must be between 1 and ${maxDays}.`);
      return;
    }
    if (n > 30 && !globalThis.confirm(`This will re-run history for ${n} days. Continue?`)) {
      return;
    }
    setError(null);
    onSubmit({ days: String(n) });
  }

  return (
    <div className='pt-4 flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <Label htmlFor='backfill-days'>Days</Label>
        <Input
          id='backfill-days'
          type='number'
          min={1}
          max={maxDays}
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
        {error && <p className='text-sm text-destructive'>{error}</p>}
      </div>
      <Button onClick={submit}>Confirm refresh</Button>
    </div>
  );
}

function MonthForm({ onSubmit }: { onSubmit: (p: Record<string, string>) => void }) {
  const today = new Date();
  const maxMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      setError('Choose a month (YYYY-MM).');
      return;
    }
    setError(null);
    onSubmit({ month });
  }

  return (
    <div className='pt-4 flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <Label htmlFor='month'>Month</Label>
        <Input
          id='month'
          type='month'
          max={maxMonth}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        {error && <p className='text-sm text-destructive'>{error}</p>}
      </div>
      <Button onClick={submit}>Confirm refresh</Button>
    </div>
  );
}
